<template>
	<div ref="lightbox" class="lightbox" tabindex="0" @keydown.esc="close" @keydown.left="navPrevious" @keydown.right="navNext">
		<transition :name="`lightbox-${transitionName}`" tag="div" mode="out-in">
			<div :key="previewIndex" class="media-container">
				<img v-if="preview.Type === 'image'" :src="`${cdnHost}${getLocaleString(preview.Image)}`" />
				<iframe
					v-if="preview.Type === 'youtube'"
					:src="`https://www.youtube.com/embed/${getLocaleString(preview.Image)}?rel=0&autoplay=1&mute=1&modestbranding=1`"
					frameborder="0"
					allowfullscreen
				/>
			</div>
		</transition>
		<div v-if="!isMobile" class="arrow previous" :class="{ disabled: !canNavPrevious }" @click="navPrevious"></div>
		<div v-if="!isMobile" class="arrow next" :class="{ disabled: !canNavNext }" @click="navNext"></div>
		<div class="close-container" @click="close">
			<svgIcon icon-holder-class="close-icon" icon-id="icon-btn-close" />
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';

export default {
	name: 'GameDetailsLightbox',
	components: {
		svgIcon,
	},
	props: {
		mediaList: {
			type: Array,
			default: () => [],
		},
		selectedPreviewIndex: {
			type: Number,
			default: -1,
		},
	},
	data() {
		return {
			previewIndex: 0,
			previousPreviewIndex: 0,
			xDown: null,
			yDown: null,
		};
	},
	computed: {
		...mapState(['isIOS', 'isAndroid']),
		preview() {
			return this.mediaList[this.previewIndex];
		},
		transitionName() {
			return this.previewIndex >= this.previousPreviewIndex ? 'next' : 'previous';
		},
		canNavPrevious() {
			return this.previewIndex !== 0;
		},
		canNavNext() {
			return this.previewIndex < this.mediaList.length - 1;
		},
		isMobile() {
			return this.isIOS || this.isAndroid;
		},
		language() {
			return this.$i18n.locale;
		},
	},
	mounted() {
		this.$refs.lightbox.focus();
		this.previewIndex = this.selectedPreviewIndex;

		this.$nextTick(() => {
			this.$refs.lightbox.addEventListener('touchstart', this.handleTouchStart, false);
			this.$refs.lightbox.addEventListener('touchmove', this.handleTouchMove, false);
		});
	},
	methods: {
		setPreviewIndex(index) {
			this.previousPreviewIndex = this.previewIndex;
			this.previewIndex = index;
			this.$emit('setPageIndex', this.preview.pageIndex);
		},
		navPrevious() {
			if (!this.canNavPrevious) return;
			this.$emit('track', 'Lightbox - Left');
			this.setPreviewIndex(this.previewIndex - 1);
		},
		navNext() {
			if (!this.canNavNext) return;
			this.$emit('track', 'Lightbox - Right');
			this.setPreviewIndex(this.previewIndex + 1);
		},
		close() {
			this.$emit('close');
		},
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
		handleTouchStart(e) {
			const { clientX, clientY } = e.touches[0];
			this.xDown = clientX;
			this.yDown = clientY;
		},
		handleTouchMove(e) {
			if (!this.xDown || !this.yDown) return;

			let xUp = e.touches[0].clientX;
			let yUp = e.touches[0].clientY;

			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;

			if (Math.abs(xDiff) > Math.abs(yDiff)) {
				if (xDiff > 0) {
					this.navNext();
				} else {
					this.navPrevious();
				}
			} else {
				if (yDiff <= 0) {
					this.close();
				}
			}

			this.xDown = null;
			this.yDown = null;
		},
	},
};
</script>

<style lang="less" scoped>
.lightbox-previous-enter-active,
.lightbox-next-enter-active {
	transition: all 1s;
}
.lightbox-previous-enter {
	opacity: 0;
	transform: translateX(-50px);
}
.lightbox-next-enter {
	opacity: 0;
	transform: translateX(50px);
}
.lightbox-previous-enter-to,
.lightbox-next-enter-to {
	opacity: 1;
}

.lightbox {
	align-items: center;
	background-color: rgba(12, 13, 14, 0.9);
	display: flex;
	height: 100%;
	justify-content: center;
	left: 0;
	position: fixed;
	top: 0;
	width: 100%;
	user-select: none;
	z-index: 500;
	.media-container {
		align-items: center;
		display: flex;
		height: 72vh;
		justify-content: center;
		width: 60vw;
		img {
			border-radius: 5px;
			max-height: 100%;
			max-width: 60vw;
		}
		iframe {
			height: 100%;
			width: 100%;
		}
	}
	.close-container {
		align-items: center;
		background-color: #282a30;
		border-radius: 5px;
		cursor: pointer;
		display: flex;
		height: 45px;
		justify-content: center;
		margin: 30px 30px 0 0;
		position: absolute;
		right: 0;
		top: 0;
		width: 45px;
		&:not(:active):hover {
			background-color: #3c3f46;
			.close-icon {
				opacity: 1;
			}
		}
		&:active {
			background-color: #202124;
			.close-icon {
				opacity: 0.3;
			}
		}
		.close-icon {
			height: 20px;
			opacity: 0.8;
			width: 20px;
		}
	}
	.arrow {
		background-color: #282a30;
		cursor: pointer;
		height: 88px;
		position: absolute;
		top: calc(50% - 44px);
		width: 42px;
		&:not(:active):not(.disabled):hover {
			background-color: #3c3f46;
			&:before {
				opacity: 1;
			}
		}
		&:not(.disabled):active {
			background-color: #202124;
			&:before {
				opacity: 0.3;
			}
		}
		&:before {
			background-position: center;
			background-repeat: no-repeat;
			content: '';
			height: 100%;
			opacity: 0.8;
			position: absolute;
			width: 100%;
		}
		&.disabled {
			cursor: default;
			opacity: 0.2;
		}
		&.previous {
			border-bottom-right-radius: 5px;
			border-top-right-radius: 5px;
			left: 0;
			&:before {
				background: url('~@/images/game-details/lightbox/previous.svg') no-repeat center;
			}
		}
		&.next {
			border-bottom-left-radius: 5px;
			border-top-left-radius: 5px;
			right: 0;
			&:before {
				background: url('~@/images/game-details/lightbox/next.svg') no-repeat center;
			}
		}
	}
}
</style>
